import type { UserInDto } from 'shared/domain/user/types/inDto';
import {
  CHART_CATALOG,
  type ChartId,
  type ChartSection,
} from '../catalog/chartCatalog';
import type {
  AnalyticsChart,
  BuildChartContext,
  ChartDefinition,
  ChartResult,
} from '../catalog/types';
import { ChartResultStatus } from '../catalog/types';
import { buildAnalyticsChart } from './buildAnalyticsChart';

function isAccessibleDefinition(
  definition: ChartDefinition,
  user: UserInDto,
  result: ChartResult | undefined
): boolean {
  if (!definition.enabled) {
    return false;
  }

  if (definition.accessMode === 'role') {
    return definition.canAccess?.(user) ?? false;
  }

  if (result?.status === ChartResultStatus.idle) {
    return false;
  }

  return true;
}

export function selectChartsForSection(
  catalog: readonly ChartDefinition[],
  results: Readonly<Partial<Record<ChartId, ChartResult>>>,
  user: UserInDto,
  ctx: BuildChartContext,
  section: ChartSection
): AnalyticsChart[] {
  return catalog
    .filter((definition) => definition.section === section)
    .filter((definition) =>
      isAccessibleDefinition(definition, user, results[definition.id as ChartId])
    )
    .sort((a, b) => a.order - b.order)
    .map((definition) => {
      const result =
        results[definition.id as ChartId] ??
        ({
          status: ChartResultStatus.idle,
          rawData: null,
          filters: undefined,
          dataGeneratedAt: null,
          error: null,
          reload: (): void => undefined,
        } satisfies ChartResult);

      return buildAnalyticsChart(definition, result, ctx);
    });
}

export function selectChartById(
  id: ChartId,
  results: Readonly<Partial<Record<ChartId, ChartResult>>>,
  user: UserInDto,
  ctx: BuildChartContext
): AnalyticsChart | undefined {
  const definition = CHART_CATALOG.find((entry) => entry.id === id);
  if (!definition) {
    return undefined;
  }

  const result = results[id];
  if (!isAccessibleDefinition(definition, user, result)) {
    return undefined;
  }

  const chartResult =
    result ??
    ({
      status: ChartResultStatus.idle,
      rawData: null,
      filters: undefined,
      dataGeneratedAt: null,
      error: null,
      reload: (): void => undefined,
    } satisfies ChartResult);

  return buildAnalyticsChart(definition, chartResult, ctx);
}

export function groupChartsBySection(
  charts: AnalyticsChart[]
): Record<ChartSection, AnalyticsChart[]> {
  const grouped = {} as Record<ChartSection, AnalyticsChart[]>;

  for (const chart of charts) {
    const section = chart.section as ChartSection;
    if (!grouped[section]) {
      grouped[section] = [];
    }
    grouped[section].push(chart);
  }

  return grouped;
}
